export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

/** True for Safari/WebKit opaque network failures ("Load failed", Failed to fetch). */
export function isUnreachableClientError(err: unknown): boolean {
  if (err instanceof ApiError) return err.status === 0;
  const name = err && typeof err === "object" && "name" in err ? String(err.name) : "";
  const raw = err instanceof Error ? err.message.trim().toLowerCase() : "";
  return (
    name === "TypeError" ||
    raw === "load failed" ||
    raw === "failed to fetch" ||
    raw === "networkerror when attempting to fetch resource" ||
    raw.includes("network request failed")
  );
}

/**
 * Safari/WebKit often surfaces network or FileReader failures as the opaque
 * string "Load failed" (Chrome says "Failed to fetch"). Map those for UI copy.
 * Do not blame attachment size here — that path has its own 413 / size-offer UI.
 */
/** Hub session or Google mail token problems — not the same as a routing/HTML glitch. */
export function isAuthApiError(err: unknown): boolean {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return true;
  const msg = (err instanceof Error ? err.message : String(err || "")).toLowerCase();
  return /sign in to continue|session expired|unauthorized|forbidden|invalid.?grant|token.*(expir|revok|invalid)|not authenticated|reconnect.*mail/i.test(
    msg,
  );
}

/**
 * Turn HTML error pages and huge bodies into short UI copy.
 * A 401 here is session expiry; "Cannot GET …" is almost never a token issue.
 */
/** Friendly copy for raw Gmail rate-limit / quota errors (strip project ids). */
export function sanitizeGmailClientMessage(raw: string): string {
  const text = raw.replace(/\s+/g, " ").trim();
  if (!text) return text;
  if (
    /quota exceeded/i.test(text) ||
    /units per minute/i.test(text) ||
    /total query cost/i.test(text) ||
    /rateLimitExceeded/i.test(text) ||
    /userRateLimitExceeded/i.test(text)
  ) {
    return "Gmail rate limit hit. Wait about a minute, then try again.";
  }
  return text
    .replace(/for consumer '[^']+'/gi, "")
    .replace(/project_number:\d+/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function normalizeApiErrorMessage(status: number, raw: string): string {
  const text = raw.trim();
  if (status === 401) {
    return "Your Hub session expired. Sign in again to continue.";
  }
  if (status === 403) {
    return "You don't have permission for this action. Try signing in again.";
  }
  const quota = sanitizeGmailClientMessage(text);
  if (quota !== text && /Gmail rate limit hit/.test(quota)) {
    return quota;
  }
  if (status === 404 && text && !/<html/i.test(text)) {
    const cleaned = sanitizeGmailClientMessage(text);
    return cleaned.length > 200 ? `${cleaned.slice(0, 197)}…` : cleaned;
  }
  if (/<!DOCTYPE\s+html|<html[\s>]/i.test(text)) {
    const cannotGet = text.match(/Cannot GET\s+(\S+)/i);
    if (cannotGet) {
      const path = cannotGet[1] || "";
      if (path.includes("/attachments/")) {
        return "Could not download this attachment from the mail server. Try again in a moment.";
      }
      return "The mail server returned an unexpected response while loading this thread. You may be seeing a saved copy. Tap Refresh, or sign out and back in if it keeps happening.";
    }
    return "The mail server returned an unexpected response. Try again.";
  }
  const cleaned = sanitizeGmailClientMessage(text);
  if (cleaned.length > 320) return `${cleaned.slice(0, 317)}…`;
  return cleaned || "Something went wrong.";
}

export function humanizeClientError(err: unknown, fallback = "Something went wrong."): string {
  if (err instanceof ApiError) {
    return sanitizeGmailClientMessage(err.message || fallback) || fallback;
  }
  const name = err && typeof err === "object" && "name" in err ? String(err.name) : "";
  const raw = err instanceof Error ? err.message.trim() : "";
  if (isUnreachableClientError(err)) {
    return "Could not reach the mail server. Check your connection and try again.";
  }
  if (raw.toLowerCase() === "the operation was aborted" || name === "AbortError") {
    return "Request was cancelled or timed out. Try again.";
  }
  return sanitizeGmailClientMessage(raw) || fallback;
}

const TOKEN_KEY = "atw-hub-token";

declare global {
  interface Window {
    __ATW_API_BASE__?: string;
  }
}

export function apiBase(): string {
  const fromWindow =
    typeof window !== "undefined" && window.__ATW_API_BASE__
      ? String(window.__ATW_API_BASE__).replace(/\/$/, "")
      : "";
  if (fromWindow) return fromWindow;
  const fromEnv = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";
  if (fromEnv) return fromEnv;
  // Production default: live Lightsail API (same host the prior SPA used).
  if (typeof window !== "undefined" && /hub\.atw\.net$/i.test(window.location.hostname)) {
    return "https://api.hub.atw.net";
  }
  return "";
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export type ApiInit = RequestInit & {
  /** Abort the request after this many ms (default: no client timeout). */
  timeoutMs?: number;
};

export async function api<T = unknown>(path: string, init?: ApiInit): Promise<T> {
  const { timeoutMs, ...rest } = init ?? {};
  const headers = new Headers(rest.headers);
  // FormData must keep the browser-generated multipart boundary — never force JSON.
  if (
    rest.body &&
    !headers.has("content-type") &&
    !(typeof FormData !== "undefined" && rest.body instanceof FormData)
  ) {
    headers.set("content-type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);

  const controller =
    typeof timeoutMs === "number" && timeoutMs > 0 ? new AbortController() : null;
  const timer =
    controller && timeoutMs
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;
  if (controller && rest.signal) {
    if (rest.signal.aborted) controller.abort();
    else {
      rest.signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }

  try {
    const response = await fetch(`${apiBase()}${path}`, {
      ...rest,
      headers,
      signal: controller?.signal ?? rest.signal,
    });
    const text = await response.text();
    let body: Record<string, unknown> = {};
    try {
      body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      body = {
        message: normalizeApiErrorMessage(response.status, text || "Invalid response"),
      };
    }

    if (response.status === 401) {
      setToken(null);
      window.dispatchEvent(new Event("atw-hub-unauthorized"));
      throw new ApiError(
        401,
        normalizeApiErrorMessage(401, String(body.message || "Sign in to continue.")),
      );
    }
    if (!response.ok) {
      throw new ApiError(
        response.status,
        normalizeApiErrorMessage(
          response.status,
          String(body.message || "Something went wrong."),
        ),
        body.details,
      );
    }
    return body as T;
  } catch (err) {
    if (
      err instanceof DOMException &&
      (err.name === "AbortError" || err.name === "TimeoutError")
    ) {
      throw new ApiError(408, "Mail request timed out. Tap refresh to try again.");
    }
    // Safari: TypeError "Load failed" on dead network / blocked POST bodies.
    if (err instanceof TypeError) {
      throw new ApiError(0, humanizeClientError(err, "Could not reach the mail server."));
    }
    throw err;
  } finally {
    if (timer != null) window.clearTimeout(timer);
  }
}

export type HubUser = {
  id?: string;
  email: string;
  name?: string;
  displayName?: string;
};

export type LoginResponse = {
  token: string;
  user: HubUser;
};

export type MailAccount = {
  id: string;
  email: string;
  providerId?: string;
  connected?: boolean;
  calendarConnected?: boolean;
  /** Google Chat scopes are on this account. */
  chatConnected?: boolean;
  /** Google Meet scopes are on this account. Scheduled Meet links still come from Calendar. */
  meetingsConnected?: boolean;
  displayName?: string;
  /** User-chosen inbox name saved on the Hub API (follows the user across devices). */
  label?: string;
};

export type MailAttachment = {
  id?: string;
  filename: string;
  mimeType?: string;
  size?: number;
  contentId?: string;
  url?: string;
  downloadUrl?: string;
  dataUrl?: string;
};

/** Tip 213: parsed calendar invite from a mail message (.ics / text/calendar). */
export type MailCalendarInvite = {
  uid: string;
  summary?: string;
  description?: string;
  location?: string;
  start?: string;
  end?: string;
  organizerEmail?: string;
  organizerName?: string;
  method?: string;
  sequence?: number;
  attachmentId?: string;
  filename?: string;
  responseStatus?: string;
};

export type MailMessage = {
  id: string;
  threadId?: string;
  accountId?: string;
  subject?: string;
  snippet?: string;
  fromName?: string;
  fromEmail?: string;
  from?: string;
  to?: string[] | string;
  cc?: string[] | string;
  bcc?: string[] | string;
  /** Addresses behind `to` / `cc` / `bcc`, which hold display names once coerced. */
  toEmails?: string[];
  ccEmails?: string[];
  bccEmails?: string[];
  date?: string;
  unread?: boolean;
  starred?: boolean;
  bodyHtml?: string;
  bodyText?: string;
  attachments?: MailAttachment[];
  labelIds?: string[];
  isDraft?: boolean;
  /** This copy is in the Sent mailbox (or Gmail's SENT label). */
  sent?: boolean;
  /** RFC Message-ID, used to collapse a sent copy and the same message in Inbox. */
  messageIdHeader?: string;
  /** Present when the message includes a calendar REQUEST (Tip 213). */
  calendarInvite?: MailCalendarInvite;
  /** The API has headers only. `body` / `bodyText` are not the message. */
  snippetOnly?: boolean;
  /** Body text came from a full provider fetch, even when it matches the snippet. */
  fullBody?: boolean;
};

export type MailThread = {
  id: string;
  threadId?: string;
  accountId?: string;
  subject?: string;
  snippet?: string;
  fromName?: string;
  fromEmail?: string;
  /** Some list/detail payloads use a single `from` contact (string or object before coerce). */
  from?: string;
  date?: string;
  unread?: boolean;
  starred?: boolean;
  messageCount?: number;
  hasAttachments?: boolean;
  messages?: MailMessage[];
  /** Mailbox address for this thread. Used to tell sent copies from mail you received. */
  accountEmail?: string;
  /** Set after a successful GET /api/mail/:id expand — single-message threads can skip refetch. */
  detailExpanded?: boolean;
  /** The provider had more messages than this payload includes. */
  chainTruncated?: boolean;
  /** Full body could not be loaded. Headers are still safe to show. */
  bodyPending?: boolean;
  bodyError?: { code?: string; retryAfterSec?: number };
};

export type MailListResponse = {
  threads?: MailThread[];
  messages?: MailMessage[];
  mail?: MailThread[];
  nextPageToken?: string;
  incomplete?: boolean;
  pageError?: { accountId?: string; code?: string; retryAfterSec?: number };
};
